import React, { useContext, useEffect, useState } from "react";
import Select from 'react-select';
import { components } from "react-select";
import { PersonContext } from "../../context/PersonContext";
import { onCreateBarn, onUpdateBarn } from "../../graphql/subscriptions";
import { Barn, BarnMember } from "../../models";
import { getBarnMembersByPersonId } from "../../utilities/barnMember/BarnMember";
import { useSubscriptionByItself } from "../../utilities/subscription/Subscription";
import ErrorAlert from "../Errors/ErrorAlert";
import { getAllBarns } from "../../utilities/barn/Barn";
import { formatAddress } from "../../utilities/address/FormatAddress";
import { getAddressById } from "../../utilities/address/Address";
import { formatPhoneNumberForDisplay } from "../../utilities/contact/FormatPhoneNumber";
import { getContactById } from "../../utilities/contact/Contact";

interface _Props {
    onSelect: Function
    selectedValue?: string
    barn?: Barn
    barnMemberOptions?: BarnMember[]
    useAllBarns?: Boolean
    useSpecificBarns?: (Barn[] | null | undefined)
    isClearable?: Boolean
}

interface formattedOption {
    label: string;
    value: string;
    barn: Barn;
    addressString: string;
    contactString: string;
    websiteString?: string;
}

/**
 * This function is passed into the select component to define what should be shown when an option is selected
 * Instead of showing the 3 lines from the option,
 * it will only show the 1 line defined below: horseName
 * @param props
 * @returns
 */
const SingleValue = (props: any) => <components.SingleValue {...props}>{props.data.horseName || props.data.label}</components.SingleValue>;

const SelectBarn: React.FC<_Props> = ({useAllBarns, useSpecificBarns, barn, barnMemberOptions, onSelect, isClearable, selectedValue}) => {
    const user = useContext(PersonContext);

    const barnSubscription = useSubscriptionByItself({
        config: {
            query: onCreateBarn,
            key: "onCreateBarn"
        }
    });

    const barnUpdateSubscription = useSubscriptionByItself({
        config: {
            query: onUpdateBarn,
            key: "onUpdateBarn"
        }
    });

    const [currentSubscriptionItem, setCurrentSubscriptionItem] = useState<any>();
    const [currentUpdateSubscriptionItem, setUpdateCurrentSubscriptionItem] = useState<any>();
    const [barnMembers, setBarnMembers] = useState<BarnMember[] | null | undefined>();
    const [formattedBarns, setFormattedBarns] = useState<formattedOption[] | null | undefined>();
    const [currentValue, setCurrentValue] = useState<formattedOption | null | undefined>();
    const [error, setError] = useState("");

    const getBarnMembers = async () => {
        if ((!!useSpecificBarns) && (useSpecificBarns.length > 0)) {
            formatBarnsFromBarns(useSpecificBarns);
        } else if (useAllBarns) {
            const queryResult = await getAllBarns();
            if (queryResult.isSuccess) {
                const allBarns: Barn[] = queryResult.result;
                formatBarnsFromBarns(allBarns);
            }
        } else if (barnMemberOptions) {
            setBarnMembers(barnMemberOptions);
            formatBarnsFromBarnMemberships(barnMemberOptions);
        } else {
            const queryResult = await getBarnMembersByPersonId(user.id);
            if (queryResult.isSuccess) {
                const memberList = queryResult.result;
                setBarnMembers(memberList);  
                formatBarnsFromBarnMemberships(memberList);          
            }
        }
    };

    const formatBarn = async (barn: Barn): Promise<formattedOption> => {
        let addressString = "Address not provided",
        contactString = "Contact not provided",
        websiteString = "Website not provided";
    
        if (barn.location) {
            addressString = formatAddress(barn.location);
        } else if (barn.barnLocationId) {
            const addressResult = await getAddressById(barn.barnLocationId);
            if (addressResult.isSuccess) {
                const address = addressResult.result;
                addressString = formatAddress(address);
            }
        }

        if (barn.contact && barn.contact.work) {
            contactString = formatPhoneNumberForDisplay(barn.contact.work);
        } else if (barn.barnContactId) {
            const contactResult = await getContactById(barn.barnContactId);
            if (contactResult.isSuccess) {
                const contact = contactResult.result;
                if (contact.work) contactString = formatPhoneNumberForDisplay(contact.work);
                if (contact.website) websiteString = contact.website;
            }
        }

        return {
            value: barn.id,
            label: barn.name,
            barn,
            addressString,
            contactString,
            websiteString
        };
    };
    
    const formatBarnsFromBarns = async (barns: Barn[]) => {
        let formattedBarns = [];
        for (var i = 0; i < barns.length; i++) {
            const barn: Barn = barns[i];
            if (barn) {
                let object: formattedOption = await formatBarn(barn);
                formattedBarns.push(object);
            }
        }
        setFormattedBarns(formattedBarns);
        if (selectedValue) findValueInList(selectedValue, formattedBarns);
    }

    const formatBarnsFromBarnMemberships = async (barnMembers: BarnMember[]) => {
        let formattedBarns = [];
        for (var i = 0; i < barnMembers.length; i++) {
            const barnMember: BarnMember = barnMembers[i];
            const barn: Barn = barnMember.barn;
            if (barn) {
                let object: formattedOption = await formatBarn(barn);
                formattedBarns.push(object);
            }
        }
        setFormattedBarns(formattedBarns);
        if (selectedValue) findValueInList(selectedValue, formattedBarns);
    }

    const findValueInList = (value: string, barnList?: formattedOption[]) => {
        let optionArray = barnList || formattedBarns;
        if (optionArray) {
            for (var i = 0; i < optionArray.length; i++) {
                const currentOption = optionArray[i];
                if (currentOption.value === value) {
                    setCurrentValue(currentOption);
                }
            }
        }
    };

    useEffect(() => {
        if (!barnMembers) getBarnMembers();
    }, [barn]);

    useEffect(() => {
        async function setValue() {
            if (selectedValue) {
                if (!formattedBarns) {
                    await getBarnMembers();
                } else {
                    findValueInList(selectedValue);
                }
                
            } else {
                setCurrentValue(undefined);
            }
        }
        setValue();
    }, [selectedValue]);

     useEffect(() => {
        if (barnSubscription && barnSubscription[0] !== undefined && barnSubscription[0] !== currentSubscriptionItem) {
            setCurrentSubscriptionItem(barnSubscription[0]);
            getBarnMembers();
        }
    }, [barnSubscription]);

    useEffect(() => {
        if (barnUpdateSubscription && barnUpdateSubscription[0] !== undefined && barnUpdateSubscription[0] !== currentUpdateSubscriptionItem) {
            setUpdateCurrentSubscriptionItem(barnUpdateSubscription[0]);
            getBarnMembers();
        }
    }, [barnUpdateSubscription]);


    const handleOnChange = async (event: any) => {
        if (event && event.barn) {
            setCurrentValue(event);
            onSelect(event.barn);
        } else {
            setCurrentValue(undefined);
            onSelect(undefined);
        }
    }

    return (
        <>
            {error && <ErrorAlert width="12" error={error} />}
            <Select
                id="select-barn"
                inputId="select-barn-input"
                menuPortalTarget={document.body}
                menuPlacement={"auto"}
                styles={{ 
                    // Fixes the overlapping problem of the component
                    menu: provided => ({ ...provided, zIndex: 9999 }),
                    // Fixes the overlapping problem of the component
                    control: baseStyles => ({
                        ...baseStyles,
                        height: baseStyles.height
                    })
                }}
                placeholder={"Select..."}
                // defaultValue={currentValue}
                value={currentValue}
                isClearable={isClearable ? true : false}
                options={formattedBarns!}
                formatOptionLabel={({ label, addressString, contactString, websiteString }) => (
                    <div className="p-0">
                        <div className="font-weight-bold text-default">{label}</div>
                        <div className="display-5 text-medium">{addressString}</div>
                        <div className="display-5 text-medium">{`${contactString} | ${websiteString}`}</div>
                    </div>
                )}
                components={{ SingleValue }}
                onChange={handleOnChange}
            />
        </>
    );
};

export default SelectBarn;
